import { useState, useCallback, useEffect } from 'react'

const MAX_ATTEMPTS = 5
const LOCKOUT_DURATION = 15 * 60 * 1000 // 15 minutos
const STORAGE_KEY = 'crm_rate_limit'

interface RateLimitState {
  attempts: number
  lockedUntil: number | null
}

function getStoredState(): RateLimitState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as RateLimitState
      // Se o lockout expirou, resetar
      if (parsed.lockedUntil && Date.now() > parsed.lockedUntil) {
        return { attempts: 0, lockedUntil: null }
      }
      return parsed
    }
  } catch {
    // ignore
  }
  return { attempts: 0, lockedUntil: null }
}

function setStoredState(state: RateLimitState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}

export function useRateLimit() {
  const [state, setState] = useState<RateLimitState>(getStoredState)

  // Recalcular isLocked baseado no tempo atual
  const isLocked = Boolean(
    state.lockedUntil && Date.now() < state.lockedUntil
  )

  const lockoutTimeLeft = isLocked && state.lockedUntil
    ? Math.max(0, Math.ceil((state.lockedUntil - Date.now()) / 1000))
    : 0

  const recordFailedAttempt = useCallback(() => {
    setState((prev) => {
      const newAttempts = prev.attempts + 1
      const shouldLock = newAttempts >= MAX_ATTEMPTS

      const newState: RateLimitState = {
        attempts: newAttempts,
        lockedUntil: shouldLock ? Date.now() + LOCKOUT_DURATION : prev.lockedUntil,
      }
      setStoredState(newState)
      return newState
    })
  }, [])

  const resetAttempts = useCallback(() => {
    const newState: RateLimitState = { attempts: 0, lockedUntil: null }
    setStoredState(newState)
    setState(newState)
  }, [])

  const getWarningMessage = useCallback((): string => {
    if (isLocked) {
      const mins = Math.ceil(lockoutTimeLeft / 60)
      return `Muitas tentativas. Tente novamente em ${mins} minuto${mins > 1 ? 's' : ''}.`
    }
    const remaining = MAX_ATTEMPTS - state.attempts
    if (remaining <= 0) {
      return 'Limite de tentativas atingido. Aguarde 15 minutos.'
    }
    return `Tentativa ${state.attempts + 1} de ${MAX_ATTEMPTS}.`
  }, [isLocked, lockoutTimeLeft, state.attempts])

  const getAlertLevel = useCallback((): 'low' | 'medium' | 'high' => {
    if (isLocked) return 'high'
    const remaining = MAX_ATTEMPTS - state.attempts
    if (remaining <= 1) return 'high'
    if (remaining <= 2) return 'medium'
    return 'low'
  }, [isLocked, state.attempts])

  // Limpar state ao desmontar (opcional — manter para persistência entre sessions)
  useEffect(() => {
    return () => {
      // Não limpar — queremos persistir entre sessions
    }
  }, [])

  return {
    attempts: state.attempts,
    isLocked,
    lockoutTimeLeft,
    recordFailedAttempt,
    resetAttempts,
    getWarningMessage,
    getAlertLevel,
  }
}
